import { isRecord } from "@arti/utilities";
import { type FC, useMemo, useState } from "react";

import type { SourceControlProviderDetail } from "../../types";

import { setupGitHubAppInstallation } from "../../api/sourceControl";
import { useToast } from "../../context/ToastContext";
import { Badge } from "../common/Badge";
import { Button } from "../common/Button";
import { ChecklistItem } from "../common/JiraSetupView";
import { PanelSection } from "../common/PanelSection";
import { Text } from "../common/Text";

interface Props {
  providerId: string;
  detail: SourceControlProviderDetail;
}

const ProviderLinkIcon: FC = () => (
  <svg
    className="h-4 w-4 text-emerald-600 dark:text-emerald-400"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
    />
  </svg>
);

function parseInstallationId(value: unknown): number | null {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string") {
    return Number.parseInt(value, 10);
  }
  return null;
}

const GitHubInstallIdentity: FC<{
  isEnabled: boolean;
  providerName: string;
}> = ({ isEnabled, providerName }) => (
  <div className="flex items-center gap-3">
    <div className="flex h-8 w-8 items-center justify-center rounded-none border border-emerald-500/20 bg-emerald-500/10">
      <ProviderLinkIcon />
    </div>
    <div>
      <h3 className="font-mono text-[11px] font-bold uppercase tracking-widest text-gray-900 dark:text-white">
        {providerName}
      </h3>
      <Text
        variant="label"
        className={
          isEnabled
            ? "text-green-600 dark:text-green-400"
            : "text-gray-400 dark:text-white/40"
        }
      >
        {isEnabled ? "Enabled" : "Disabled"}
      </Text>
    </div>
  </div>
);

const GitHubInstallSummary: FC<{
  connectedRepositoryCount: number;
  isEnabled: boolean;
  isInstalled: boolean;
  providerName: string;
}> = ({ connectedRepositoryCount, isEnabled, isInstalled, providerName }) => (
  <div className="rounded-none border border-slate-300 bg-slate-50/70 p-4 dark:border-slate-700 dark:bg-arti-panel">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <GitHubInstallIdentity
        isEnabled={isEnabled}
        providerName={providerName}
      />

      <div className="flex flex-wrap gap-2">
        <Badge variant={isInstalled ? "success" : "default"} size="sm">
          {isInstalled ? "Installed" : "Not installed"}
        </Badge>
        <Badge variant="status" size="sm">
          {connectedRepositoryCount} repos in Arti
        </Badge>
      </div>
    </div>

    <div className="mt-3 rounded-none border border-slate-300 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-950/60">
      <Text className="leading-relaxed text-slate-600 dark:text-slate-300">
        Repository discovery is scoped to the repositories granted during GitHub
        App installation. If an expected repository is missing, re-run install
        and widen repository access.
      </Text>
    </div>
  </div>
);

export const GitHubAppInstallCard: FC<Props> = ({ providerId, detail }) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);

  const githubApp = useMemo(() => {
    if (!isRecord(detail.provider.metadata)) {
      return null;
    }
    const { metadata } = detail.provider;
    return isRecord(metadata.github_app) ? metadata.github_app : null;
  }, [detail.provider.metadata]);

  const installationId = parseInstallationId(githubApp?.installation_id);

  const organization =
    typeof githubApp?.organization === "string" && githubApp.organization.trim()
      ? githubApp.organization
      : null;

  const isInstalled = Number.isFinite(installationId ?? NaN);
  const connectedRepositoryCount = detail.repositories.length;

  const handleInstall = async () => {
    try {
      setLoading(true);
      const redirectUrl = `${window.location.pathname}${window.location.search}`;
      const result = await setupGitHubAppInstallation(providerId, {
        redirect_url: redirectUrl,
      });
      window.location.href = result.redirect_url;
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.present({
        body: message,
        title: "GitHub App install failed",
        tone: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PanelSection title="GitHub App Installation">
      <div className="space-y-4">
        {/* Status card */}
        <GitHubInstallSummary
          connectedRepositoryCount={connectedRepositoryCount}
          isEnabled={detail.provider.is_enabled}
          isInstalled={isInstalled}
          providerName={detail.provider.display_name}
        />

        {/* Setup checklist */}
        <div className="space-y-2">
          <ChecklistItem
            label="App installed"
            detail={isInstalled ? "Installed" : "Not installed"}
            ok={isInstalled}
          />
          {organization && (
            <ChecklistItem
              label="Organization"
              detail={organization}
              ok={isInstalled}
            />
          )}
          {isInstalled && installationId !== null && (
            <ChecklistItem
              label="Installation ID"
              detail={String(installationId)}
              ok
            />
          )}
          <ChecklistItem
            label="Repository visibility"
            detail={
              isInstalled
                ? "Only granted repositories appear in discovery"
                : "Install required before repositories can be discovered"
            }
            ok={isInstalled}
          />
          <ChecklistItem
            label="Registered repositories"
            detail={`${connectedRepositoryCount} connected in Arti`}
            ok={isInstalled}
          />
        </div>

        <Button
          type="button"
          variant="primary"
          onClick={() => void handleInstall()}
          loading={loading}
          loadingText={isInstalled ? "Reinstalling…" : "Installing…"}
        >
          {isInstalled ? "Reinstall App" : "Install App"}
        </Button>
      </div>
    </PanelSection>
  );
};
